import { Button, Modal, Tooltip, TreeSelect } from 'antd'
import React, { useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { API } from 'src/utils/interceptor'
import { saveAs } from 'file-saver'
import { buildCsvString } from 'src/utils/csv'
import { formatFavouriteForCsv } from 'src/utils/parseShoppingListCsvRow'
import { VENUES_REPORT_DESCRIPTIONS } from './venuesReportDescriptions'
import ReportGroupSelect from './ReportGroupSelect'

export const SHOPPING_LIST_VENUE_CONFIRM_COUNT = 40

export const shouldConfirmShoppingListSize = count =>
  Number(count) > SHOPPING_LIST_VENUE_CONFIRM_COUNT

export const shoppingListProgressLine = (index, total) =>
  `Venue ${index} of ${total}`

export const shoppingListLargeGroupQuestion = count =>
  `This shopping list has ${count} venues. Generate it now?`

const ShoppingListDownload = ({ venues }) => {
  const [selectedVenueIds, setSelectedVenuesIds] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState('')

  const venueOptions = venues?.map(item => ({
    title: item.title,
    value: item._id,
  }))
  const allIds = venueOptions?.map(({ value }) => value)

  const handleDownloadCSV = async () => {
    setIsLoading(true)

    try {
      const rows = []
      const headers = [
        'Venue Name',
        'Product',
        'Zone',
        'Default qty',
        'Size',
        'Price',
        'Special price',
        'Has special price',
        'Priced By',
        'Supplier',
        'SKU',
        'Type',
        'Favourite',
        'Nominal Code',
      ]

      // Add header row
      rows.push(headers)

      // Fetch products for each selected venue
      for (let index = 0; index < selectedVenueIds.length; index += 1) {
        const venueId = selectedVenueIds[index]
        const venue = venues.find(v => v._id === venueId)
        const venueName = venue?.title || 'Unknown Venue'
        setProgress(
          shoppingListProgressLine(index + 1, selectedVenueIds.length),
        )

        try {
          const venueProducts = await API.get(
            'api',
            `venue/${venueId}/products`,
          )

          venueProducts?.forEach(product => {
            const { size, price, sku, pricedBy } = product.product || {}
            const brakesPrice = product.brakesPrice || 0

            // Determine if special price is active (TRUE/FALSE)
            const hasActiveSpecialPrice =
              product.specialPrice != null &&
              !isNaN(product.specialPrice) &&
              Number(product.specialPrice) > 0

            const specialPriceFlag = hasActiveSpecialPrice ? 'TRUE' : 'FALSE'

            // Format special price display value (numeric)
            const specialPriceValue = hasActiveSpecialPrice
              ? Number(product.specialPrice).toFixed(2)
              : '0.00'

            rows.push([
              venueName,
              product.title,
              product.zone || '',
              product.defaultQuantity || '',
              size || '',
              brakesPrice
                ? Number(brakesPrice).toFixed(2)
                : price
                ? Number(price).toFixed(2)
                : '0.00',
              specialPriceValue,
              specialPriceFlag,
              pricedBy || '',
              product.supplierTitle || '',
              sku || '',
              product.type || '',
              formatFavouriteForCsv(product.isFavourite),
              product.nominalCode || '',
            ])
          })
        } catch (error) {
          console.error(
            `Error fetching products for venue ${venueName}:`,
            error,
          )
          // Continue with other venues even if one fails
        }
      }

      // If no data was collected, add just headers
      if (rows.length === 1) {
        // Only headers, no data rows
        console.warn('No shopping list items found for selected venues')
      }

      const csvString = buildCsvString(rows)
      const timestamp = moment().format('YYYY-MM-DD_HH-mm')
      const venueCount = selectedVenueIds.length
      const filename = `shopping-list_multi_${venueCount}-venues_${timestamp}.csv`

      const blob = new Blob([csvString], {
        type: 'text/csv;charset=utf-8;',
      })
      saveAs(blob, filename)
    } catch (err) {
      console.error('Shopping List CSV download failed:', err)
      alert(
        'Failed to generate Shopping List Download. Please try again or contact support.',
      )
    } finally {
      setProgress('')
      setIsLoading(false)
    }
  }

  const askThenDownload = () => {
    if (!shouldConfirmShoppingListSize(selectedVenueIds.length)) {
      handleDownloadCSV()
      return
    }
    Modal.confirm({
      title: 'Large shopping list',
      content: shoppingListLargeGroupQuestion(selectedVenueIds.length),
      okText: 'Generate report',
      cancelText: 'Cancel',
      onOk: () => handleDownloadCSV(),
    })
  }

  return (
    <StyledBlock>
      <HelperText data-testid='report-helper'>
        {VENUES_REPORT_DESCRIPTIONS['Shopping List Download']}
      </HelperText>
      <ReportGroupSelect venues={venues} onPick={setSelectedVenuesIds} />
      <TreeSelect
        treeNodeFilterProp='title'
        allowClear={true}
        placeholder='Select Venues ...'
        treeCheckable={true}
        showCheckedStrategy={TreeSelect.SHOW_CHILD}
        dropdownStyle={{ maxHeight: '300px' }}
        style={{ marginBottom: '15px', width: '355px' }}
        onChange={ids => setSelectedVenuesIds(ids)}
        value={selectedVenueIds}
        treeData={[
          {
            title:
              selectedVenueIds.length > 0 ? (
                <span
                  onClick={() => setSelectedVenuesIds([])}
                  style={{
                    display: 'inline-block',
                    color: '#b5d922',
                    cursor: 'pointer',
                  }}
                >
                  Unselect all
                </span>
              ) : (
                <span
                  onClick={() => setSelectedVenuesIds(allIds)}
                  style={{
                    display: 'inline-block',
                    color: '#b5d922',
                    cursor: 'pointer',
                  }}
                >
                  Select all
                </span>
              ),
            value: 'xxx',
            disableCheckbox: true,
            disabled: true,
          },
          ...venueOptions,
        ]}
      />
      {selectedVenueIds.length === 0 ? (
        <Tooltip
          title={
            'Select at least one venue to generate the Shopping List Download.'
          }
        >
          <Button
            style={{ width: '355px', marginTop: 20 }}
            className='ant-btn ant-btn-primary'
          >
            Generate Report
          </Button>
        </Tooltip>
      ) : (
        <Button
          loading={isLoading}
          style={{ width: '355px', marginTop: 20 }}
          className='ant-btn ant-btn-primary'
          onClick={askThenDownload}
          disabled={isLoading}
        >
          Generate Report
        </Button>
      )}
      {progress ? <p>{progress}</p> : null}
    </StyledBlock>
  )
}

const StyledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const HelperText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`

export default ShoppingListDownload
